import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import type { MarkerStats } from '@/types';
import api from '@/services/api';
import { useAdminVerification } from '@/hooks/useAdminVerification';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<MarkerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const { verifyAdmin } = useAdminVerification();

  useEffect(() => {
    const initDashboard = async () => {
      // Verifica i permessi admin prima di caricare i dati
      const hasAdminAccess = await verifyAdmin();
      if (hasAdminAccess) {
        fetchStats();
      }
    };

    initDashboard();
  }, [verifyAdmin]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/markers/stats');
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Dashboard Amministratore
          </h1>
          <p className="text-gray-600">
            Panoramica delle segnalazioni e gestione del sistema
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Link
            to="/admin/markers"
            className="bg-blue-600 hover:bg-blue-700 text-white p-6 rounded-lg transition-colors"
          >
            <div className="text-2xl mb-2">📍</div>
            <div className="font-semibold">Gestisci Segnalazioni</div>
            <div className="text-sm opacity-90">Visualizza e modifica tutte le segnalazioni</div>
          </Link>

          <Link
            to="/admin/markers/new"
            className="bg-green-600 hover:bg-green-700 text-white p-6 rounded-lg transition-colors"
          >
            <div className="text-2xl mb-2">➕</div>
            <div className="font-semibold">Nuova Segnalazione</div>
            <div className="text-sm opacity-90">Aggiungi manualmente una segnalazione</div>
          </Link>

          <Link
            to="/admin/users"
            className="bg-orange-600 hover:bg-orange-700 text-white p-6 rounded-lg transition-colors"
          >
            <div className="text-2xl mb-2">👥</div>
            <div className="font-semibold">Gestisci Utenti</div>
            <div className="text-sm opacity-90">Amministra gli utenti del sistema</div>
          </Link>

          <Link
            to="/map"
            className="bg-purple-600 hover:bg-purple-700 text-white p-6 rounded-lg transition-colors"
          >
            <div className="text-2xl mb-2">🗺️</div>
            <div className="font-semibold">Visualizza Mappa</div>
            <div className="text-sm opacity-90">Vedi tutte le segnalazioni sulla mappa</div>
          </Link>
        </div>

        {/* Stats */}
        {stats && (
          <div className="space-y-8">
            {/* General Stats */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Statistiche Generali</h2>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="text-2xl font-bold text-blue-600 mb-2">
                    {stats.general.totalMarkers}
                  </div>
                  <div className="text-gray-600">Segnalazioni Totali</div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="text-2xl font-bold text-green-600 mb-2">
                    {stats.general.verifiedMarkers}
                  </div>
                  <div className="text-gray-600">Verificate</div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="text-2xl font-bold text-yellow-600 mb-2">
                    {stats.general.unverifiedMarkers}
                  </div>
                  <div className="text-gray-600">Da Verificare</div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="text-2xl font-bold text-red-600 mb-2">
                    {stats.general.markersWithActiveReports}
                  </div>
                  <div className="text-gray-600">Con Problemi Attivi</div>
                </div>
              </div>
            </div>

            {/* Technology Stats */}
            {stats.technologies.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Tecnologie</h2>
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="grid md:grid-cols-3 gap-4">
                    {stats.technologies.slice(0, 6).map((tech) => (
                      <div key={tech._id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                        <span className="font-medium">{tech._id || 'Non specificata'}</span>
                        <span className="text-gray-600">{tech.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Report Stats */}
            {stats.reports.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Stati delle Segnalazioni</h2>
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="grid md:grid-cols-4 gap-4">
                    {stats.reports.map((report) => (
                      <div key={report._id} className="text-center p-4 bg-gray-50 rounded">
                        <div className="text-xl font-bold mb-2">{report.count}</div>
                        <div className="text-sm text-gray-600">
                          {report._id === 'open' ? 'Aperte' :
                           report._id === 'in_progress' ? 'In Corso' :
                           report._id === 'resolved' ? 'Risolte' : 'Chiuse'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;